import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CurrentUser,
  type DocumentsState,
  documentPdfUrl,
  documents as loadDocuments,
  READ_ONLY_DOCUMENT_TYPES,
  type ReadOnlyDocumentType,
  SIGNABLE_DOCUMENT_TYPES,
  type SignableDocumentType,
  uploadDocumentSignature,
} from '../api.ts';
import { InfoTip } from '../components/InfoTip.tsx';
import { formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { errorMessage } from '../messages.ts';

/**
 * I documenti dell'ente (AN, mock 5 `o-docs`).
 *
 * "Due documenti, non uno. La nomina a responsabile è un obbligo di legge
 * dell'ente, non un adempimento nostro." Due schede, come nel mock:
 *
 *  - **Da firmare**: contratto di servizio e nomina a responsabile del
 *    trattamento (art. 28 GDPR). Il pulsante scarica una copia pulita da
 *    stampare; sotto, chi coordina carica la scansione o foto della pagina
 *    firmata. "Senza [la nomina firmata], il servizio non si attiva" — è la
 *    quarta condizione di `tenantReadiness`, mostrata in "Piattaforma" al
 *    fornitore, non qui: questa schermata non deve ripetere quella frase come
 *    se fosse lei a decidere l'accensione del widget;
 *  - **Da conservare**: elenco dei sub-responsabili, informativa da pubblicare
 *    sul proprio sito, misure di sicurezza adottate. Solo un pulsante "PDF"
 *    per ciascuno: nessun caricamento, nessuno stato da salvare.
 *
 * L'accesso impersonato del fornitore (sezione 9bis) vede la stessa
 * schermata, senza il modulo di caricamento: `editable` lo dice, come in
 * `admin-branding.ts`.
 */

type Props = { user: CurrentUser; headingRef?: React.RefObject<HTMLHeadingElement | null> };

const SIGNABLE_LABEL_KEYS: Record<SignableDocumentType, string> = {
  service_contract: 'documents.type_service_contract',
  dpa_appointment: 'documents.type_dpa_appointment',
};

const READ_ONLY_LABEL_KEYS: Record<ReadOnlyDocumentType, string> = {
  sub_processors: 'documents.type_sub_processors',
  privacy_notice: 'documents.type_privacy_notice',
  security_measures: 'documents.type_security_measures',
};

export function DocumentsPage({ user, headingRef }: Props) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<DocumentsState>();
  const [failed, setFailed] = useState(false);
  const [uploading, setUploading] = useState<SignableDocumentType>();
  const { say, announcer } = useAnnounce();
  const inputRefs = useRef<Partial<Record<SignableDocumentType, HTMLInputElement | null>>>({});

  const load = useCallback(async () => {
    try {
      setState(await loadDocuments());
      setFailed(false);
    } catch {
      setFailed(true);
      say(t('documents.load_failed'), 'danger');
    }
  }, [say, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const locale = i18n.resolvedLanguage;

  const onFileChosen = async (type: SignableDocumentType, file: File | undefined) => {
    if (!file) return;
    setUploading(type);
    try {
      const result = await uploadDocumentSignature(type, file);
      setState((current) =>
        current
          ? {
              ...current,
              signable: current.signable.map((entry) =>
                entry.type === type ? { ...entry, signed: result.signed } : entry,
              ),
            }
          : current,
      );
      say(t('documents.upload_success', { type: t(SIGNABLE_LABEL_KEYS[type]) }));
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setUploading(undefined);
      const input = inputRefs.current[type];
      if (input) input.value = '';
    }
  };

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('documents.title')}
      </h2>
      <p className="card__sub">{t('documents.description')}</p>

      {announcer}
      {failed ? (
        <p>
          <button type="button" className="button" onClick={() => void load()}>
            {t('shifts.retry')}
          </button>
        </p>
      ) : null}

      <div className="hours-layout">
        <section className="card" aria-labelledby="documents-signable-heading">
          <h3 id="documents-signable-heading">{t('documents.to_sign_title')}</h3>
          {state?.editable === false ? <p className="note">{t('documents.read_only')}</p> : null}

          {state === undefined ? (
            <>
              <p className="visually-hidden">{t('documents.loading')}</p>
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--wide" aria-hidden="true" />
            </>
          ) : (
            <ul className="plain-list">
              {SIGNABLE_DOCUMENT_TYPES.map((type) => {
                const entry = state.signable.find((row) => row.type === type);
                const signed = entry?.signed ?? null;
                return (
                  <li key={type} className="row">
                    <div>
                      <p className="row__title">{t(SIGNABLE_LABEL_KEYS[type])}</p>
                      <p className={signed ? 'note' : 'muted'}>
                        {signed
                          ? t('documents.signed_status', {
                              date: formatWhen(signed.uploadedAt, locale, user.tenant?.timezone),
                            })
                          : t('documents.not_signed_status')}
                      </p>
                    </div>
                    <div className="row__actions">
                      <a
                        className="button"
                        href={documentPdfUrl(type)}
                        aria-label={t('documents.download_label', {
                          type: t(SIGNABLE_LABEL_KEYS[type]),
                        })}
                      >
                        {t('documents.download_pdf')}
                      </a>
                      {state.editable ? (
                        <label className="button">
                          {uploading === type
                            ? t('documents.uploading')
                            : t('documents.upload_button')}
                          <input
                            ref={(node) => {
                              inputRefs.current[type] = node;
                            }}
                            type="file"
                            accept="application/pdf,image/jpeg,image/png"
                            disabled={uploading !== undefined}
                            className="visually-hidden"
                            aria-label={t('documents.upload_label', {
                              type: t(SIGNABLE_LABEL_KEYS[type]),
                            })}
                            onChange={(event) => void onFileChosen(type, event.target.files?.[0])}
                          />
                        </label>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card" aria-labelledby="documents-reference-heading">
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="documents-reference-heading">{t('documents.to_conserve_title')}</h3>
              <InfoTip>{t('documents.to_conserve_hint')}</InfoTip>
            </div>
          </div>
          <ul className="plain-list">
            {READ_ONLY_DOCUMENT_TYPES.map((type) => (
              <li key={type} className="row">
                <p className="row__title">{t(READ_ONLY_LABEL_KEYS[type])}</p>
                <a
                  className="button"
                  href={documentPdfUrl(type)}
                  aria-label={t('documents.download_label', {
                    type: t(READ_ONLY_LABEL_KEYS[type]),
                  })}
                >
                  {t('documents.download_pdf')}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
